import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * One tooltip for every `title` in the app.
 *
 * ## Why replace the native one at all
 *
 * The browser's tooltip is unstyleable, arrives after about a second, renders
 * in the OS rather than the page, and looks like a 1998 desktop application
 * sitting on top of a dark interface. Every `title` in this codebase was
 * written to explain something - what a host pick does, why a track is marked E
 * - and all of them were being delivered badly.
 *
 * ## It steals the attribute rather than trying to suppress it
 *
 * There is no way to stop a native tooltip except to remove the attribute, so
 * that is what this does: on hover the `title` moves to `data-gv-title`, and on
 * leave it moves back. Restoring matters - `title` is part of the accessible
 * name computation, and an app that quietly stripped every one of them would be
 * worse for screen readers than the ugly tooltip was.
 *
 * ## In the top layer, because half of them are inside dialogs
 *
 * The crate's "play next", the settings drawer, the badge rows - a tooltip
 * positioned in normal flow paints *underneath* a `<dialog showModal>` however
 * high its z-index, because the top layer is above all of it. The Popover API
 * puts this in the top layer too. Where it is unsupported the element still
 * renders and still positions; it just loses that one argument, which is a
 * better failure than not appearing.
 *
 * ## One element, one listener, delegated
 *
 * Not a component per tooltip. There are dozens of `title`s across the app and
 * a wrapper around each would be dozens of extra nodes and dozens of hook
 * subscriptions to render text that is usually never read.
 */

/** Long enough not to flash while the cursor crosses things on its way past. */
const OPEN_DELAY_MS = 120;

/** How far from the element the tooltip sits, and from the viewport edge. */
const GAP = 8;
const MARGIN = 8;

/**
 * How long after opening a scroll is assumed to be the one that opened it.
 *
 * Focus scrolls its target into view within a frame or two; a person cannot
 * start scrolling that fast after tabbing.
 */
const SETTLE_MS = 250;

export function Tooltip() {
  const tip = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  /*
   * Navigation is the one exit that fires no pointer event.
   *
   * Clicking a link moves the page out from under the cursor without a
   * `pointerout`, which would leave a tooltip describing something that is no
   * longer on screen. Everything else - leaving, clicking, scrolling, tabbing
   * away, Escape - is covered by a listener below.
   */
  useEffect(() => {
    const node = tip.current;

    if (!node) return;

    node.classList.remove('gv-tip-open');

    try {
      node.hidePopover();
    } catch {
      // Not open. Nothing to do.
    }
  }, [pathname]);

  useEffect(() => {
    const node = tip.current;

    if (!node) return;

    let host: HTMLElement | null = null;
    let timer: number | undefined;
    /** When the current tooltip opened - see `onScroll`. */
    let openedAt = 0;

    /** Put the attribute back on whatever we took it from. */
    const restore = () => {
      if (host?.dataset.gvTitle !== undefined) {
        host.setAttribute('title', host.dataset.gvTitle);
        delete host.dataset.gvTitle;
      }
    };

    const hide = () => {
      window.clearTimeout(timer);
      restore();
      host = null;
      node.classList.remove('gv-tip-open');

      try {
        node.hidePopover();
      } catch {
        // Not open, or popovers are unsupported. Either way there is nothing
        // to close, and the class above is what actually hides it.
      }
    };

    const place = () => {
      if (!host) return;

      const anchor = host.getBoundingClientRect();
      const box = node.getBoundingClientRect();

      /*
       * Above by default, below when there is no room.
       *
       * Above is the convention and it keeps the pointer clear of the tooltip,
       * which matters because the tooltip is not interactive and hovering it
       * would otherwise count as leaving the thing it describes.
       */
      const above = anchor.top - box.height - GAP;
      const top = above < MARGIN ? anchor.bottom + GAP : above;

      const centred = anchor.left + anchor.width / 2 - box.width / 2;
      const left = Math.max(
        MARGIN,
        Math.min(centred, window.innerWidth - box.width - MARGIN),
      );

      node.style.top = `${Math.round(top)}px`;
      node.style.left = `${Math.round(left)}px`;
    };

    const show = (target: HTMLElement) => {
      const text = target.getAttribute('title');

      if (text === null || text.trim() === '') return;

      /*
       * `iframe` is not a tooltip.
       *
       * Its `title` is a required accessible name for the embedded document -
       * "Overlay preview" - and showing it on hover would put a label over
       * somebody's own preview every time they moved the mouse across it.
       */
      if (target.tagName === 'IFRAME') return;

      restore();

      host = target;
      target.dataset.gvTitle = text;
      target.removeAttribute('title');

      node.textContent = text;

      try {
        node.showPopover();
      } catch {
        // Unsupported, or already open. The element is `position: fixed`
        // regardless, so it is visible either way.
      }

      /*
       * Positioned before it is revealed, animated after.
       *
       * Measuring needs the element laid out, and laying it out where it does
       * not belong for one frame is a visible jump. `gv-tip-open` is added on
       * the next frame, which is also what starts the animation.
       */
      place();
      openedAt = performance.now();
      requestAnimationFrame(() => node.classList.add('gv-tip-open'));
    };

    const onOver = (event: Event) => {
      const target = (event.target as Element | null)?.closest?.('[title]');

      if (!(target instanceof HTMLElement)) {
        if (host) hide();

        return;
      }

      if (target === host) return;

      window.clearTimeout(timer);
      // Restore immediately on the way out of the previous one, so moving
      // between two neighbours does not leave the first without its attribute.
      restore();
      host = null;
      node.classList.remove('gv-tip-open');

      timer = window.setTimeout(() => show(target), OPEN_DELAY_MS);
    };

    /*
     * Keyboard gets it with no delay.
     *
     * A delay exists to stop tooltips flashing past under a moving cursor.
     * Focus does not move by accident, so waiting would only be a wait.
     */
    const onFocus = (event: Event) => {
      const target = (event.target as Element | null)?.closest?.('[title]');

      if (target instanceof HTMLElement && target !== host) {
        window.clearTimeout(timer);
        show(target);
      }
    };

    /*
     * Escape hides this and keeps going.
     *
     * The layered-dismissal convention says the topmost thing should swallow
     * the key, and for a popover or a dialog that is right - both are things
     * somebody deliberately opened. A tooltip is not: it is usually open
     * because a cursor is resting somewhere, and swallowing Escape would mean
     * a host trying to close the crate found that the first press did nothing
     * they could see. So this dismisses itself and lets the dialog have the
     * key too.
     */
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };

    /*
     * Scrolling dismisses it - except the scroll that opened it.
     *
     * Hiding on any scroll looks reasonable and breaks the keyboard: tabbing to
     * something below the fold scrolls it into view, and that scroll would
     * cancel the tooltip the focus had just opened, so a keyboard user would
     * never see one.
     *
     * Repositioning instead of hiding was the first attempt and is worse than
     * it sounds: scrolling drags fresh elements under a stationary cursor,
     * which fires `pointerover` and reassigns the anchor mid-scroll. A grace
     * window is less clever and behaves predictably - a scroll a person started
     * still dismisses, because they cannot have started it within a frame of
     * focus landing.
     */
    const onScroll = () => {
      if (performance.now() - openedAt > SETTLE_MS) hide();
    };

    document.addEventListener('pointerover', onOver, true);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('focusout', hide, true);
    document.addEventListener('keydown', onKey, true);
    // Capture, so this also sees a pane scrolling inside the page rather than
    // only the document.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('blur', hide);

    return () => {
      hide();
      document.removeEventListener('pointerover', onOver, true);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('focusin', onFocus, true);
      document.removeEventListener('focusout', hide, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  return (
    <div
      ref={tip}
      // `manual`, not `auto`: an auto popover closes on any outside click and
      // dismisses its own light dismiss stack, which would fight the dialogs
      // this appears over.
      popover="manual"
      role="tooltip"
      data-gv-tooltip
      // Never the reason a click misses. It is decoration over live controls.
      className="gv-tip"
    />
  );
}
